// =============================================================================
//  offer-key.ts — the identity a product's offers are filed under.
//
//  Every row in `product_offers` is keyed by (offer_group_key, region_code,
//  merchant_domain). This file owns the FIRST of those three, and it has
//  exactly one job: produce the same string the applied SQL produces.
//
//  ┌─ supabase/migrations/0034_product_offers.sql, get_category_products/3 ─┐
//  │  CASE WHEN NULLIF(p.asin, '') IS NOT NULL                             │
//  │       THEN 'asin:' || p.asin                                          │
//  │       ELSE 'name:' || p.normalized_name                               │
//  │  END                                                                  │
//  └───────────────────────────────────────────────────────────────────────┘
//
//  🚨 THE EMPTY-STRING TRAP. `products.asin` is a nullable text column and a
//  meaningful number of rows carry '' rather than NULL (a page JSON that lost
//  its ASIN writes `product.ASIN ?? null`, but an upstream '' survives). The
//  SQL's NULLIF sends '' down the NAME branch. JavaScript truthiness does the
//  same thing for free — which is exactly why this file tests `if (asin)` and
//  NOT `if (asin != null)`. Get that wrong and the writer files an offer under
//  'asin:' while the reader looks for 'name:…': the row is written, billed for,
//  and PERMANENTLY INVISIBLE. Nothing errors. Nothing logs. It just never shows.
//
//  🚨 AND THE SECOND HALF OF THE TRAP: the ASIN is NOT trimmed. The SQL
//  concatenates `p.asin` verbatim, so a stored ' B0ABC12345 ' keys
//  'asin: B0ABC12345 ' on the database side. Trimming here would produce
//  'asin:B0ABC12345' — a different string, and the same silent invisibility.
//  A whitespace-padded ASIN is a data-hygiene bug worth fixing at the source;
//  it is NOT worth fixing here, where the only requirement is agreement.
//
//  WHICH FUNCTION TO CALL:
//    offerGroupKeyFromRow  — whenever you hold the `products` ROW. It is the
//                            authority: `upsertIntrinsic` writes
//                            `asin = COALESCE(new, products.asin)`, so a row can
//                            carry an ASIN the in-memory Product never had, and
//                            only the row agrees with the SQL in that case.
//    offerGroupKey         — when all you have is the in-memory Product (the
//                            Amazon refresh path, the find-product path).
//
//  Keying note, accepted at design time: two non-ASIN products that are the same
//  physical thing under translated titles split into one group per language of a
//  country. Bounded by the refresh caps; ASIN covers the buyable majority.
// =============================================================================

import { normalizeProductName } from "../../supabase/normalize-product-name";

/** The `asin:` / `name:` discriminators, so callers never hand-roll a prefix. */
export const OFFER_KEY_ASIN_PREFIX = "asin:";
export const OFFER_KEY_NAME_PREFIX = "name:";

/**
 * The offer group key for an in-memory product.
 *
 * Returns null when the product can be keyed neither way (no ASIN and a title
 * that normalizes to nothing) — callers must treat null as "this product cannot
 * hold offers" and skip, never substitute a placeholder.
 */
export function offerGroupKey(product: {
  ASIN?: string | null;
  title?: string | null;
}): string | null {
  // Truthiness, deliberately: '' and null and undefined all fall through to the
  // name branch, matching NULLIF(p.asin,'') IS NOT NULL. Not trimmed —
  // see the header.
  if (product.ASIN) return `${OFFER_KEY_ASIN_PREFIX}${product.ASIN}`;
  const normalized = normalizeProductName(product.title ?? "");
  return normalized ? `${OFFER_KEY_NAME_PREFIX}${normalized}` : null;
}

/**
 * The offer group key for a `products` table row — the DB-side vocabulary
 * (snake_case, `normalized_name` already computed by the writer).
 *
 * Prefer this over `offerGroupKey` whenever the row is available: it reads the
 * same two columns the SQL reads, so it cannot disagree with it.
 */
export function offerGroupKeyFromRow(row: {
  asin?: string | null;
  normalized_name?: string | null;
}): string | null {
  if (row.asin) return `${OFFER_KEY_ASIN_PREFIX}${row.asin}`;
  const normalized = row.normalized_name?.trim();
  return normalized ? `${OFFER_KEY_NAME_PREFIX}${normalized}` : null;
}

/** True when the key identifies a product by ASIN rather than by name. */
export function isAsinOfferGroupKey(key: string): boolean {
  return key.startsWith(OFFER_KEY_ASIN_PREFIX);
}
